import React, { useEffect, useState } from 'react';
import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportsCohortItem from '../../atoms/ReportsCohortItem';
import { Close } from '@HomaHealth/ui-component-library';
import componentConstants from '../../../core-utils/messages/componentConstants';
import { ICohortDetails } from '../CreateNewModalCoreComponent';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';
import Loader from '../../molecules/Loader';

export interface CohortDetailsModalProps {
  open: boolean;
  isPage?: boolean;
  handleClose: () => any;
  setOpenCohortDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  subTitle: string;
  handleSave?: () => any;
  isSaveButtonDisabled?: boolean;
  totalResults: string;
  startDate?: string;
  endDate?: string;
  employerGroup?: string[];
  isLoading?: boolean;
  type?: string;
}

const useStyles = makeStyles<{ isSaveButtonDisabled: boolean }>()(
  (_theme, { isSaveButtonDisabled }) => ({
    root: {
      width: '1110px',
      height: '591px',
      backgroundColor: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px 24px 24px 32px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
    },
    cohortDetailsContainer: {
      overflowY: 'auto',
      ...scrollBarStyles,
    },
    cohortItem: {
      width: '656px',
      height: '170px',
      background: '#FBFBFE',
      borderRadius: '8px',
      marginBottom: '16px',
      padding: '16px 20px',
    },
    cancelButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    saveButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: `1px solid ${
        isSaveButtonDisabled
          ? patientAccessTheme.palette.grey[500]
          : patientAccessTheme.palette.primary[500]
      }`,
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    totalResultsGrid: {
      position: 'absolute',
      right: '60px',
      display: 'flex',
      justifyContent: 'center',
      padding: '10px 26px',
      borderRadius: '8px',
      marginTop: '49px',
      backgroundColor: patientAccessTheme.palette.primary[400],
      width: '300px',
    },
    previewResults: {
      position: 'absolute',
      right: '240px',
    },
  }),
);

const CohortDetailsModal: React.FC<CohortDetailsModalProps> = ({
  open,
  isPage,
  handleClose,
  setOpenCohortDetailsModal,
  handleSave,
  title,
  subTitle,
  isSaveButtonDisabled = true,
  totalResults,
  startDate,
  endDate,
  employerGroup,
  isLoading,
  type,
}) => {
  //constants
  const { classes } = useStyles({ isSaveButtonDisabled });
  const constants = componentConstants.ORGANISMS.COHORT_DETAILS;
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const cohortDetailsList = toJS(cohortDetailsStore.cohortDetailsList);

  const [propertyDropDownOptions, setPropertyDropDownOptions] = useState<
    string[][]
  >([]);
  const [categoryDropDownOptions, setCategoryDropDownOptions] = useState<
    string[]
  >([]);
  const [conditionDropDownOptions, setConditionDropDownOptions] = useState<
    string[][]
  >([]);
  const [cohortSelectionsValues, setCohortSelectionsValues] = useState<
    string[]
  >([]);
  // const [subCategoryDropDownOptions, setSubCategoryDropDownOptions] = useState<
  //   string[][]
  // >([]);

  const handleAddCohort = () => {
    const cohort: ICohortDetails = {
      id: cohortDetailsList.length + 1,
      category: '',
      condition: '',
      property: '',
      results: '',
      dropdownSelectionTypeIndex: 0,
      andOrCondition: 'AND',
      propertiesPageNumber: 0,
      totalPagesForProperties: 0,
    };
    cohortDetailsStore.setCohortDetailsList([...cohortDetailsList, cohort]);
  };

  const handleDeleteCohort = (itemIndex: number, refresh?: boolean) => {
    if (refresh) {
      const updatedList = cohortDetailsList.map((cohort, index) => {
        if (index === itemIndex) {
          return {
            ...cohort,
            category: '',
            condition: '',
            property: '',
            subCategory: '',
            results: '',
            multipleProperties: [],
          };
        }

        return cohort;
      });

      cohortDetailsStore.setCohortDetailsList(updatedList);
    } else {
      const updatedList = cohortDetailsList.filter(
        (_, index) => index !== itemIndex,
      );

      cohortDetailsStore.setCohortDetailsList(updatedList);
    }
  };

  const handleClearFilter = () => {
    cohortDetailsStore.setCohortDetailsList([
      {
        id: 1,
        category: '',
        condition: '',
        property: '',
        results: '',
        dropdownSelectionTypeIndex: 0,
        andOrCondition: 'AND',
        propertiesPageNumber: 0,
        totalPagesForProperties: 0,
      },
    ]);

    cohortDetailsStore.removeConditionsDetails();
    cohortDetailsStore.removeClaimIdList();
    cohortDetailsStore.removeMemberUniqueIdList();
  };

  const getCohortDetails = async () => {
    let conditionDetail = '';
    const conditionsDetails: string[] = [];
    cohortDetailsStore.cohortDetailsList?.map((detail, index) => {
      conditionDetail = '';
      conditionDetail += detail?.category + ' ' + detail?.condition + ' ';
      if (detail?.property) {
        conditionDetail += detail?.property + ' ';
      } else if (detail?.multipleProperties) {
        conditionDetail +=
          detail?.multipleProperties?.toString()?.replaceAll(',', ' | ') + ' ';
      }
      if (conditionDetail.trim() !== '') {
        conditionsDetails?.push(conditionDetail);
      }
      if (
        cohortDetailsStore.cohortDetailsList?.length - 1 !== index &&
        detail?.andOrCondition
      ) {
        conditionsDetails?.push(detail?.andOrCondition);
      }
    });
    cohortDetailsStore.setConditionsDetails(conditionsDetails);
  };

  useEffect(() => {
    // setPropertyDropDownOptions(
    //   JSON.parse(
    //     LZString.decompress(
    //       cohortDetailsStore.propertyDropDownOptionsCompressedString,
    //     ),
    //   ),
    // );
    setCategoryDropDownOptions(
      toJS(cohortDetailsStore.categoryDropDownOptions),
    );
    setConditionDropDownOptions(
      toJS(cohortDetailsStore.conditionDropDownOptions),
    );
    setCohortSelectionsValues(toJS(cohortDetailsStore.cohortSelectionsTypes));
    // setSubCategoryDropDownOptions(
    //   toJS(cohortDetailsStore.subCategoryDropDownOptions),
    // );
  }, [cohortDetailsStore.memberUniqueIdList]);

  return (
    <Modal
      style={{ zIndex: 500 }}
      open={open}
      onClose={() => setOpenCohortDetailsModal(false)}
    >
      {isLoading ? (
        <Grid container className={classes.root}>
          <Loader width="1110px" height="591px" />
        </Grid>
      ) : (
        <Grid container className={classes.root}>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                fontSize="18px"
                lineHeight="26px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin="0px 0px 4px 0px"
              >
                {title}
              </Typography>
            </Grid>
            <Grid style={{ height: '14px', width: '14px' }}>
              <Close
                height={'14px'}
                width={'14px'}
                fill={patientAccessTheme.palette.status.icon}
                onClick={() => setOpenCohortDetailsModal(false)}
                cursor={'pointer'}
              />
            </Grid>
          </Grid>

          <Grid>
            <Typography
              fontSize="14px"
              fontType="regular"
              lineHeight="25px"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              margin="0px 0px 24px 0px"
            >
              {subTitle}
            </Typography>
          </Grid>
          <Grid className={classes.previewResults}>
            <Typography
              fontSize="16px"
              fontType="medium"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.grey[900]}
            >
              {constants.PREVIEW_RESULTS}
            </Typography>
          </Grid>
          <Grid className={classes.totalResultsGrid}>
            <Typography
              fontSize="12px"
              fontType="semiBold"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.status.white}
            >
              {constants.TOTAL_PATIENTS}
            </Typography>
            <Typography
              fontSize="14px"
              fontType="semiBold"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.status.white}
            >
              {totalResults} {'(100%)'}
            </Typography>
          </Grid>
          <Grid
            className={classes.cohortDetailsContainer}
            style={{
              height: cohortDetailsList?.length > 1 ? '436px' : '186px',
            }}
          >
            <ReportsCohortItem
              handleDeleteCohort={handleDeleteCohort}
              propertyDropDownOptions={propertyDropDownOptions}
              setPropertyDropDownOptions={setPropertyDropDownOptions}
              categoryDropDownOptions={categoryDropDownOptions}
              // subCategoryDropDownOptions={subCategoryDropDownOptions}
              conditionDropDownOptions={conditionDropDownOptions}
              cohortSelectionsValues={cohortSelectionsValues}
              totalResults={totalResults}
              startDate={startDate}
              endDate={endDate}
              employerGroup={employerGroup}
              type={type}
            />
          </Grid>
          <Grid style={{ display: 'flex' }}>
            <Typography
              fontSize="14px"
              lineHeight="28px"
              cursor="pointer"
              fontType="medium"
              textColor={patientAccessTheme.palette.primary[500]}
              onClick={handleAddCohort}
            >
              {constants.ADD_COHORTS}
            </Typography>
          </Grid>
          <Grid
            container
            style={{
              position: 'absolute',
              bottom: 0,
              margin: '0px 0px 16px 645px',
            }}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid style={{ marginRight: '12px' }}>
              <Button
                label={constants.RESET_FILTER}
                variant="outlined"
                className={classes.cancelButton}
                onClick={handleClearFilter}
              />
            </Grid>
            <Grid style={{ marginRight: '12px' }}>
              <Button
                label={constants.CANCEL}
                variant="outlined"
                className={classes.cancelButton}
                onClick={handleClose}
              />
            </Grid>
            <Grid>
              <Button
                label={constants.SAVE}
                variant="contained"
                className={classes.saveButton}
                disabled={
                  isPage
                    ? cohortDetailsStore?.claimIdList?.length === 0 &&
                      cohortDetailsStore?.memberUniqueIdList?.length === 0
                    : isSaveButtonDisabled
                }
                onClick={() => {
                  setOpenCohortDetailsModal(false);
                  handleSave && handleSave();
                  getCohortDetails();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};

export default CohortDetailsModal;
